/**
 * ⭐ As partes do ato do imóvel, na tela — 16/10/2026 (PRD §51).
 *
 * ⚠️ **Espelho de `api/src/lib/partes-do-imovel.ts`**, para a prévia do passo
 * "Conferir" mostrar a frase como ela sai no relatório. Quem escreve a frase
 * que vai ao documento é o servidor; e `npm run testar-fichas-de-imoveis`
 * importa ESTE arquivo e confere que os rótulos, os verbos e a frase dizem o
 * mesmo dos dois lados. Cópia sem conferência diverge na primeira correção.
 */
import type { PapelDaParteNoAto } from './api';
import { documentoNaTela, soDigitos } from './documento';

/**
 * O papel da OUTRA parte, o verbo do investigado que sai dele e o grupo na lista.
 * ⭐ A lista dele desde 27/10/2026 (PRD §65), na ordem dele. `verbo: null`: a
 * parte sai na segunda frase, com o papel. `grupo: null`: a lista antiga.
 */
export const PAPEIS_DA_PARTE: Array<{ chave: PapelDaParteNoAto; rotulo: string; verbo: string | null; grupo: string | null }> = [
  { chave: 'promitente_vendedor', rotulo: 'Promitente vendedor', verbo: 'comprometeu-se a adquirir o bem de', grupo: 'Compra, venda e transmissão' },
  { chave: 'promitente_comprador', rotulo: 'Promitente comprador', verbo: 'comprometeu-se a vender o bem a', grupo: 'Compra, venda e transmissão' },
  { chave: 'alienante', rotulo: 'Alienante', verbo: 'adquiriu de', grupo: 'Compra, venda e transmissão' },
  { chave: 'adquirente', rotulo: 'Adquirente', verbo: 'alienou o bem a', grupo: 'Compra, venda e transmissão' },
  { chave: 'permutante', rotulo: 'Permutante', verbo: 'figurou em permuta com', grupo: 'Compra, venda e transmissão' },
  { chave: 'doador', rotulo: 'Doador', verbo: 'recebeu o bem em doação de', grupo: 'Compra, venda e transmissão' },
  { chave: 'donatario', rotulo: 'Donatário', verbo: 'doou o bem a', grupo: 'Compra, venda e transmissão' },
  { chave: 'cedente', rotulo: 'Cedente', verbo: 'recebeu em cessão direitos de', grupo: 'Compra, venda e transmissão' },
  { chave: 'cessionario', rotulo: 'Cessionário', verbo: 'cedeu direitos a', grupo: 'Compra, venda e transmissão' },
  { chave: 'interveniente_anuente', rotulo: 'Interveniente anuente', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'interveniente_quitante', rotulo: 'Interveniente quitante', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'garantidor', rotulo: 'Garantidor', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'avalista', rotulo: 'Avalista', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'fiador', rotulo: 'Fiador', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'credor_hipotecario', rotulo: 'Credor hipotecário', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'devedor_hipotecario', rotulo: 'Devedor hipotecário', verbo: 'recebeu hipoteca sobre o bem de', grupo: 'Intervenção e garantias' },
  { chave: 'garantidor_hipotecario', rotulo: 'Garantidor hipotecário', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'fiduciante', rotulo: 'Fiduciante', verbo: 'recebeu em garantia, por alienação fiduciária, a propriedade resolúvel do bem de', grupo: 'Intervenção e garantias' },
  { chave: 'fiduciario', rotulo: 'Fiduciário', verbo: 'transferiu em garantia, por alienação fiduciária, a propriedade resolúvel do bem a', grupo: 'Intervenção e garantias' },
  { chave: 'terceiro_garantidor', rotulo: 'Terceiro garantidor', verbo: null, grupo: 'Intervenção e garantias' },
  { chave: 'credor_pignoraticio', rotulo: 'Credor pignoratício', verbo: 'constituiu penhor, registrado na matrícula, em favor de', grupo: 'Intervenção e garantias' },
  { chave: 'proprietario', rotulo: 'Proprietário', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'coproprietario', rotulo: 'Coproprietário', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'nu_proprietario', rotulo: 'Nu-proprietário', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'usufrutuario', rotulo: 'Usufrutuário', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'instituidor_de_usufruto', rotulo: 'Instituidor de usufruto', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'titular_de_direito_real_de_habitacao', rotulo: 'Titular de direito real de habitação', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'concedente_de_direito_de_superficie', rotulo: 'Concedente de direito de superfície', verbo: 'recebeu direito de superfície sobre o bem de', grupo: 'Propriedade e direitos reais' },
  { chave: 'superficiario', rotulo: 'Superficiário', verbo: 'concedeu direito de superfície sobre o bem a', grupo: 'Propriedade e direitos reais' },
  { chave: 'titular_de_servidao', rotulo: 'Titular de servidão', verbo: null, grupo: 'Propriedade e direitos reais' },
  { chave: 'falecido', rotulo: 'Falecido (de cujus)', verbo: null, grupo: 'Sucessão' },
  { chave: 'herdeiro', rotulo: 'Herdeiro', verbo: null, grupo: 'Sucessão' },
  { chave: 'legatario', rotulo: 'Legatário', verbo: null, grupo: 'Sucessão' },
  { chave: 'inventariante', rotulo: 'Inventariante', verbo: null, grupo: 'Sucessão' },
  { chave: 'meeiro', rotulo: 'Meeiro', verbo: null, grupo: 'Sucessão' },
  { chave: 'espolio', rotulo: 'Espólio', verbo: null, grupo: 'Sucessão' },
  { chave: 'renunciante_de_heranca', rotulo: 'Renunciante de herança', verbo: null, grupo: 'Sucessão' },
  { chave: 'cedente_de_direitos_hereditarios', rotulo: 'Cedente de direitos hereditários', verbo: 'recebeu em cessão os direitos hereditários de', grupo: 'Sucessão' },
  { chave: 'cessionario_de_direitos_hereditarios', rotulo: 'Cessionário de direitos hereditários', verbo: 'cedeu os direitos hereditários a', grupo: 'Sucessão' },
  { chave: 'outorgante', rotulo: 'Outorgante', verbo: null, grupo: 'Representação' },
  { chave: 'outorgado', rotulo: 'Outorgado', verbo: null, grupo: 'Representação' },
  { chave: 'procurador', rotulo: 'Procurador', verbo: null, grupo: 'Representação' },
  { chave: 'substabelecente', rotulo: 'Substabelecente', verbo: null, grupo: 'Representação' },
  { chave: 'substabelecido', rotulo: 'Substabelecido', verbo: null, grupo: 'Representação' },
  { chave: 'representante_legal', rotulo: 'Representante legal', verbo: null, grupo: 'Representação' },
  { chave: 'representado', rotulo: 'Representado', verbo: null, grupo: 'Representação' },
  { chave: 'administrador_judicial', rotulo: 'Administrador judicial', verbo: null, grupo: 'Representação' },
  { chave: 'conjuge', rotulo: 'Cônjuge', verbo: null, grupo: 'Família' },
  { chave: 'companheiro', rotulo: 'Companheiro(a)', verbo: null, grupo: 'Família' },
  { chave: 'anuente_conjugal', rotulo: 'Anuente conjugal', verbo: null, grupo: 'Família' },
  { chave: 'socio', rotulo: 'Sócio', verbo: null, grupo: 'Sociedades' },
  { chave: 'socio_retirante', rotulo: 'Sócio retirante', verbo: null, grupo: 'Sociedades' },
  { chave: 'acionista', rotulo: 'Acionista', verbo: null, grupo: 'Sociedades' },
  { chave: 'administrador', rotulo: 'Administrador', verbo: null, grupo: 'Sociedades' },
  { chave: 'cedente_de_quotas', rotulo: 'Cedente de quotas', verbo: 'recebeu em cessão as quotas de', grupo: 'Sociedades' },
  { chave: 'cessionario_de_quotas', rotulo: 'Cessionário de quotas', verbo: 'cedeu as quotas a', grupo: 'Sociedades' },
  { chave: 'incorporadora', rotulo: 'Incorporadora', verbo: null, grupo: 'Sociedades' },
  { chave: 'incorporada', rotulo: 'Incorporada', verbo: null, grupo: 'Sociedades' },
  { chave: 'empresa_cindida', rotulo: 'Empresa cindida', verbo: null, grupo: 'Sociedades' },
  { chave: 'sociedade_sucessora', rotulo: 'Sociedade sucessora', verbo: null, grupo: 'Sociedades' },
  { chave: 'exequente', rotulo: 'Exequente', verbo: null, grupo: 'Processo judicial' },
  { chave: 'executado', rotulo: 'Executado', verbo: null, grupo: 'Processo judicial' },
  { chave: 'penhorante', rotulo: 'Penhorante', verbo: null, grupo: 'Processo judicial' },
  { chave: 'depositario_judicial', rotulo: 'Depositário judicial', verbo: null, grupo: 'Processo judicial' },
  { chave: 'arrematante', rotulo: 'Arrematante', verbo: 'teve o bem arrematado por', grupo: 'Processo judicial' },
  { chave: 'adjudicante', rotulo: 'Adjudicante', verbo: null, grupo: 'Processo judicial' },
  { chave: 'requerente', rotulo: 'Requerente', verbo: null, grupo: 'Processo judicial' },
  { chave: 'requerido', rotulo: 'Requerido', verbo: null, grupo: 'Processo judicial' },
  { chave: 'locador', rotulo: 'Locador', verbo: 'tomou o bem em locação de', grupo: 'Contratos' },
  { chave: 'locatario', rotulo: 'Locatário', verbo: 'deu o bem em locação a', grupo: 'Contratos' },
  { chave: 'arrendador', rotulo: 'Arrendador', verbo: 'tomou o bem em arrendamento de', grupo: 'Contratos' },
  { chave: 'arrendatario', rotulo: 'Arrendatário', verbo: 'deu o bem em arrendamento a', grupo: 'Contratos' },
  { chave: 'comodante', rotulo: 'Comodante', verbo: 'recebeu o bem em comodato de', grupo: 'Contratos' },
  { chave: 'comodatario', rotulo: 'Comodatário', verbo: 'deu o bem em comodato a', grupo: 'Contratos' },
  { chave: 'distratante', rotulo: 'Distratante', verbo: null, grupo: 'Contratos' },
  { chave: 'beneficiario', rotulo: 'Beneficiário', verbo: null, grupo: 'Outros' },
  { chave: 'outro', rotulo: 'Outro', verbo: 'participou do ato com', grupo: 'Outros' },
  { chave: 'vendedor', rotulo: 'Vendedor', verbo: 'adquiriu de', grupo: null },
  { chave: 'comprador', rotulo: 'Comprador', verbo: 'vendeu o bem a', grupo: null },
  { chave: 'credor_fiduciario', rotulo: 'Credor fiduciário', verbo: 'deu o bem em alienação fiduciária a', grupo: null },
  { chave: 'devedor_fiduciante', rotulo: 'Devedor fiduciante', verbo: 'recebeu o bem em alienação fiduciária de', grupo: null },
];

export const ROTULO_DO_PAPEL_DA_PARTE = Object.fromEntries(PAPEIS_DA_PARTE.map((p) => [p.chave, p.rotulo])) as Record<
  PapelDaParteNoAto,
  string
>;

/** "CPF 111.444.777-35", "CNPJ 11.222.333/0001-81", ou nulo. */
export function documentoComRotulo(documento: string | null | undefined): string | null {
  const d = soDigitos(documento ?? '');
  if (!d) return null;
  if (d.length === 11) return `CPF ${documentoNaTela(d)}`;
  if (d.length === 14) return `CNPJ ${documentoNaTela(d)}`;
  return d;
}

function emLista(itens: string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join(', ')} e ${itens.at(-1)}`;
}

/** A frase do ato, como o servidor a escreve. Nula quando não diria nada além do nome. */
export function fraseDoAto(
  investigado: string,
  partes: Array<{ nome: string; documento: string | null; papel: PapelDaParteNoAto | null }>,
): string | null {
  const validas = partes.filter((p) => p.nome.trim().length > 0);
  if (validas.length === 0 || !validas.some((p) => p.papel || documentoComRotulo(p.documento))) return null;
  const grupos: Array<{ verbo: string; nomes: string[] }> = [];
  const semVerbo: string[] = [];
  for (const p of validas) {
    const papel = PAPEIS_DA_PARTE.find((x) => x.chave === (p.papel ?? 'outro'))!;
    const doc = documentoComRotulo(p.documento);
    const nome = doc ? `${p.nome} (${doc})` : p.nome;
    if (papel.verbo === null) {
      semVerbo.push(`${nome}, ${papel.rotulo.toLocaleLowerCase('pt-BR')}`);
      continue;
    }
    const g = grupos.find((x) => x.verbo === papel.verbo);
    if (g) g.nomes.push(nome);
    else grupos.push({ verbo: papel.verbo, nomes: [nome] });
  }
  if (grupos.length === 0) return `${investigado} participou do ato com ${emListaLonga(semVerbo)}.`;
  const primeira = `${investigado} ${emLista(grupos.map((g) => `${g.verbo} ${emLista(g.nomes)}`))}.`;
  if (semVerbo.length === 0) return primeira;
  return semVerbo.length === 1
    ? `${primeira} Também participou do ato ${semVerbo[0]}.`
    : `${primeira} Também participaram do ato: ${emListaLonga(semVerbo)}.`;
}

function emListaLonga(itens: string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join('; ')}; e ${itens.at(-1)}`;
}
